import { INTERNAL_EMAIL_RECORD_TYPE } from './internalEmailConversations';

/**
 * The conversation a surface asked to be scrolled to and flashed after switching
 * tabs — see `CandidateContext.goToView`.
 *
 * Carries record ids rather than one id because an internal email fans out to one
 * `AtsUserMessage` per teammate, and the Conversations row is filed against
 * whichever copy recorded it: normally the primary, but a sibling takes over when
 * the primary's own send failed (`ConversationRecorder#writes_the_row?`). The
 * activity knows every copy's id, so it hands them all over and the match holds in
 * both cases.
 */
export interface ConversationHighlight {
  recordType: string;
  recordIds: (number | string)[];
}

// `!= null` in spirit, spelled out for eqeqeq: 0 and "" are real ids, so a
// truthiness check would drop them. Doubles as the type guard the filters need.
const isPresent = (value?: number | string | null): value is number | string =>
  value !== null && value !== undefined;

const sameId = (a?: number | string | null, b?: number | string | null) =>
  isPresent(a) && isPresent(b) && String(a) === String(b);

/** True when this row is the one the caller asked to highlight. */
export const matchesHighlight = (
  conversation: { record?: { id?: number | string; type?: string } | null },
  highlight?: ConversationHighlight | null
) => {
  if (!highlight?.recordType || !highlight?.recordIds?.length) return false;
  if (conversation?.record?.type !== highlight.recordType) return false;

  return highlight.recordIds.some((id) => sameId(id, conversation?.record?.id));
};

/**
 * The accordion index the highlighted row lives under — Scheduled is 0,
 * Conversation History is 1, and only the latter is open by default. A jump has to
 * open the right one or it lands on a collapsed section.
 */
export const SCHEDULED_SECTION_INDEX = 0;
export const HISTORY_SECTION_INDEX = 1;

export const sectionIndexFor = (conversation: {
  sent_at?: string | null;
  scheduled_at?: string | null;
}) =>
  !conversation?.sent_at && conversation?.scheduled_at
    ? SCHEDULED_SECTION_INDEX
    : HISTORY_SECTION_INDEX;

/** DOM id of a conversation row, so the jump has something to scroll to. */
export const conversationAnchorId = (messageId?: number | string | null) =>
  isPresent(messageId) ? `conversation-message-${messageId}` : undefined;

/** An internal email's highlight target, built from the activity's own data. */
export const internalEmailHighlight = (activityData: {
  internal_email_id?: number | string;
  messages?: { id?: number | string }[];
}): ConversationHighlight => {
  const copyIds = (activityData?.messages ?? [])
    .map((message) => message?.id)
    .filter(isPresent);

  return {
    recordType: INTERNAL_EMAIL_RECORD_TYPE,
    recordIds: copyIds.length
      ? copyIds
      : [activityData?.internal_email_id].filter(isPresent),
  };
};
